import React from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';

// Only styles is imported from input-field due to type conflicts
import inputStyles from '../forms/input-field.css';
import styles from './date-picker.css';

export type Placements =
  | 'auto'
  | 'top-start'
  | 'auto-start'
  | 'auto-end'
  | 'top'
  | 'top-end'
  | 'right-start'
  | 'right'
  | 'right-end'
  | 'bottom-end'
  | 'bottom'
  | 'bottom-start'
  | 'left-end'
  | 'left'
  | 'left-start';

export interface DatePickerProps {
  /** Formats date */
  dateFormat?: string | string[];
  /** Disables navigation by keyboard */
  disabledKeyboardNavigation?: boolean;
  /** Restricts the height of the popup to be constant throughout */
  fixedHeight?: boolean;
  /** Custom callback to keep track of selected date */
  handleChange?: () => void;
  /** Minimum date can be set to restrict past dates or start dates */
  minDate?: Date;
  /** Maximum date can be set to restrict future dates or end dates */
  maxDate?: Date;
  /** Callback when date or time is clicked to trigger change */
  onChange: (date: Date) => void;
  /** Callback when the calendar is closed */
  onCalendarClose?: () => void;
  /** Callback when date or time is entered via input value to trigger change */
  onChangeRaw?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Placeholder text, can only be used if selected is null */
  placeholderText?: string;
  /** Allows for alternate popup placements */
  popperPlacement?: Placements;
  /** Indicates date selected */
  selected?: Date | null;
  /** Closes popup after a date is selected */
  shouldCloseOnSelect?: boolean;
  /** Shows the navigation triangle on disabled months */
  showDisabledMonthNavigation?: boolean;
  /** Allows for time to be selected  */
  showTimeSelect?: boolean;
  /** Formats time */
  timeFormat?: string;
  /** Allows for different time intervals */
  timeIntervals?: number;
  /** Treat date values as UTC rather than the browser's local time */
  utc?: boolean;
  /** Allows to add a unique identifier to th element */
  id?: string;
  /** Provides a data-testid */
  'data-testid'?: string;
  /** Provides a visual indicator to show what dates are selected in the date range by moving the start date */
  selectsEnd?: boolean;
  /** Provides a visual indicator to show what dates are selected in the date range by moving the end date */
  selectsStart?: boolean;
  /** A way to pass custom classes to cutomize the styling of the Date in the date picker */
  dayClassName?: (date: Date) => string | null;
  /** Start Date for the Date Range Picker */
  startDate?: Date | null;
  /** End Date for the Date Range Picker */
  endDate?: Date | null;
  /** Disables button, disallowing interaction */
  disabled?: boolean;
}

const MS_PER_MINUTE = 1000 * 60;

// The datepicker is in the user's local time zone and does not accept any other time zone, so
// we apply this conversion to let the user interact with the datepicker as if it was in UTC.
const localDateAsUtc = (date: Date) =>
  new Date(date.getTime() - date.getTimezoneOffset() * MS_PER_MINUTE);

const utcDateAsLocal = (date: Date) =>
  new Date(date.getTime() + date.getTimezoneOffset() * MS_PER_MINUTE);

/**
 * DatePicker component that wraps ReactDatePicker
 *
 * @param {DatePickerProps} props
 *
 * @returns JSX.ELEMENT
 */
export const DatePicker = ({
  onChange,
  onCalendarClose,
  id,
  minDate,
  maxDate,
  'data-testid': dataTestId,
  startDate,
  endDate,
  dayClassName,
  selected = null,
  placeholderText = 'dd-mm-yyyy',
  dateFormat = 'dd-MM-yyyy',
  showTimeSelect = false,
  disabledKeyboardNavigation = false,
  fixedHeight = true,
  popperPlacement = 'top-start',
  shouldCloseOnSelect = false,
  showDisabledMonthNavigation = true,
  timeFormat = 'h:mm aa',
  timeIntervals = 30,
  utc,
  selectsEnd = false,
  selectsStart = false,
  disabled = false
}: DatePickerProps) => {
  const utcProps: Partial<DatePickerProps> = {};

  if (utc) {
    // Convert input date values to the user's local time
    if (selected instanceof Date) {
      utcProps.selected = utcDateAsLocal(selected);
    }

    if (startDate instanceof Date) {
      utcProps.startDate = utcDateAsLocal(startDate);
    }

    if (endDate instanceof Date) {
      utcProps.endDate = utcDateAsLocal(endDate);
    }

    if (minDate instanceof Date) {
      utcProps.minDate = utcDateAsLocal(minDate);
    }

    if (maxDate instanceof Date) {
      utcProps.maxDate = utcDateAsLocal(maxDate);
    }

    // Convert output date values to UTC
    utcProps.onChange = (value: Date) => onChange(utc ? localDateAsUtc(value) : value);

    if (typeof dayClassName === 'function') {
      utcProps.dayClassName = (value: Date) => dayClassName(utc ? localDateAsUtc(value) : value);
    }
  }

  return (
    <div className={styles.datePicker}>
      <ReactDatePicker
        data-testid={dataTestId}
        id={id}
        className={inputStyles.input}
        onChange={onChange}
        onCalendarClose={onCalendarClose}
        selected={selected}
        dateFormat={dateFormat}
        placeholderText={placeholderText}
        showTimeSelect={showTimeSelect}
        popperPlacement={popperPlacement}
        shouldCloseOnSelect={shouldCloseOnSelect}
        showDisabledMonthNavigation={showDisabledMonthNavigation}
        timeFormat={timeFormat}
        timeIntervals={timeIntervals}
        fixedHeight={fixedHeight}
        disabledKeyboardNavigation={disabledKeyboardNavigation}
        minDate={minDate}
        maxDate={maxDate}
        selectsEnd={selectsEnd}
        selectsStart={selectsStart}
        dayClassName={dayClassName}
        startDate={startDate}
        endDate={endDate}
        disabled={disabled}
        {...utcProps}
      />
    </div>
  );
};

export default DatePicker;
